// batch-46 contract (FO-388) — PURE ketenregeling-teller (art. 7:668a BW).
//
// Input: de geordende bepaalde-/onbepaalde-tijd-contracthistorie van één
// medewerker (afgeleid uit EmployeeContractMutation + de vervangtId-keten door de
// API-laag). Output: aantal contracten in de huidige keten, totale ketenduur,
// resetmomenten en of het volgende contract wettelijk voor onbepaalde tijd is.
//
// Hoofdregel: 3 opeenvolgende bepaalde-tijd-contracten OF een ketenduur > 36
// maanden → het volgende contract is van rechtswege onbepaalde tijd. Een
// onderbreking van MEER dan 6 maanden reset de keten (tussenpozen ≤ 6 mnd tellen
// mee, inclusief in de 36-maanden-som). Parameters staan als constanten in
// `@streep/types` (later instelbaar). Dependency-vrij unit-testbaar.

import { KETENREGELING_PARAMS, type KetenregelingParams } from '@streep/types'

export interface KetenContract {
  /** Ingangsdatum van het contract (UTC-middernacht van de NL-kalenderdag). */
  startDatum: Date
  /** Einddatum; null = onbepaalde tijd (of nog lopend). */
  eindDatum: Date | null
  /** true = bepaalde tijd (telt mee in de keten); false = onbepaalde tijd. */
  bepaaldeTijd: boolean
}

export interface KetenregelingResultaat {
  /** Aantal bepaalde-tijd-contracten in de huidige (niet-onderbroken) keten. */
  aantalContracten: number
  /** Totale ketenduur in hele maanden (eerste start t/m laatste eind, incl. gaps ≤ 6 mnd). */
  ketenduurMaanden: number
  /** Startdata waar een onderbreking > 6 mnd de keten resette. */
  resetMomenten: Date[]
  /** Het volgende contract is wettelijk voor onbepaalde tijd (3e of > 36 mnd). */
  volgendVerplichtOnbepaald: boolean
  /**
   * De historie is mogelijk onvolledig (geen mutaties vóór de v3-migratie): de
   * caller moet "mogelijk onvolledig - controleer het papieren dossier" tonen
   * i.p.v. een vals-zeker getal (§5.6, nooit gokken).
   */
  mogelijkOnvolledig: boolean
}

/**
 * Hele maanden tussen twee op NL-middernacht (UTC opgeslagen) genormaliseerde
 * datums, afgerond naar beneden op volle maanden, minimaal 0. UTC-componenten
 * zodat het resultaat niet van de process-tijdzone afhangt (contractdatums worden
 * als UTC-middernacht van de kalenderdag bewaard, `new Date('yyyy-mm-dd')`).
 *
 * `eind` telt EXCLUSIEF (het interval [start, eind)). Voor een contract-/keten-
 * looptijd - waar de einddatum de laatste INCLUSIEVE contractdag is - gebruik
 * `contractDuurMaandenInclusief`.
 */
export function heleMaandenTussen(start: Date, eind: Date): number {
  const maanden =
    (eind.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (eind.getUTCMonth() - start.getUTCMonth())
  const dagCorrectie = eind.getUTCDate() >= start.getUTCDate() ? 0 : -1
  return Math.max(0, maanden + dagCorrectie)
}

/** `d` + `n` kalenderdagen (UTC). */
function addUtcDagen(d: Date, n: number): Date {
  const r = new Date(d.getTime())
  r.setUTCDate(r.getUTCDate() + n)
  return r
}

/**
 * Contract-/keten-looptijd in hele maanden waarbij `eind` de LAATSTE INCLUSIEVE
 * dag is. Een contract 1 jan t/m 30 jun is een contract van 6 maanden (niet 5):
 * we meten [start, eind + 1 dag). Dit spiegelt hoe de rest van het systeem de
 * einddatum opvat (de verval-cron verloopt pas op `eindDatum < vandaag`, dus op
 * de einddatum zelf loopt het contract nog).
 */
export function contractDuurMaandenInclusief(start: Date, eind: Date): number {
  return heleMaandenTussen(start, addUtcDagen(eind, 1))
}

/**
 * `van` + `maanden` kalendermaanden (UTC), voor de onderbreking-grens. Klemt op
 * de laatste dag van de doelmaand zodat een maand-einde-datum niet overloopt
 * (31 aug + 6 mnd = 28/29 feb, niet 2/3 mrt).
 */
function maandenErbij(van: Date, maanden: number): Date {
  const doelJaar = van.getUTCFullYear()
  const doelMaand = van.getUTCMonth() + maanden
  // Laatste dag van de doelmaand (dag 0 van de maand erna).
  const laatsteDag = new Date(Date.UTC(doelJaar, doelMaand + 1, 0)).getUTCDate()
  const dag = Math.min(van.getUTCDate(), laatsteDag)
  return new Date(Date.UTC(doelJaar, doelMaand, dag))
}

/**
 * Aanzegplicht-predicaat (art. 7:668 BW): een bepaalde-tijd-contract met een
 * looptijd ≥ `minMaanden` (default 6) is aanzegplichtig. Onbepaalde tijd of geen
 * einddatum → geen aanzegplicht. De looptijd wordt INCLUSIEF de einddatum gemeten
 * (zie `contractDuurMaandenInclusief`) zodat een contract dat op de laatste dag
 * van de 6e maand eindigt (1 jan t/m 30 jun) correct aanzegplichtig is.
 */
export function isAanzegplichtig(
  contract: { bepaaldeTijd: boolean; startDatum: Date; eindDatum: Date | null },
  minMaanden = 6,
): boolean {
  if (!contract.bepaaldeTijd || !contract.eindDatum) return false
  return contractDuurMaandenInclusief(contract.startDatum, contract.eindDatum) >= minMaanden
}

/**
 * Bereken de ketenregeling-stand. Puur: geen DB/IO. `contracten` hoeft niet
 * gesorteerd te zijn (wordt op startdatum geordend). `historieMogelijkOnvolledig`
 * markeert dat de bron-historie mogelijk incompleet is (bv. geen mutaties vóór de
 * v3-migratie) → de teller wordt als onbetrouwbaar gemarkeerd.
 */
export function berekenKetenregeling(input: {
  contracten: KetenContract[]
  params?: KetenregelingParams
  historieMogelijkOnvolledig?: boolean
}): KetenregelingResultaat {
  const params = input.params ?? KETENREGELING_PARAMS
  const contracten = [...input.contracten].sort(
    (a, b) => a.startDatum.getTime() - b.startDatum.getTime(),
  )
  const mogelijkOnvolledig = Boolean(input.historieMogelijkOnvolledig) || contracten.length === 0

  if (contracten.length === 0) {
    return {
      aantalContracten: 0,
      ketenduurMaanden: 0,
      resetMomenten: [],
      volgendVerplichtOnbepaald: false,
      mogelijkOnvolledig,
    }
  }

  // Walk de historie; een onderbreking > `onderbrekingResetMaanden` start een
  // verse keten. `chainStartIdx` wijst naar het eerste contract van de HUIDIGE
  // keten (die met het meest recente contract).
  const resetMomenten: Date[] = []
  let chainStartIdx = 0
  for (let i = 1; i < contracten.length; i++) {
    const vorige = contracten[i - 1]
    const huidige = contracten[i]
    // Zonder einddatum op de vorige (onbepaalde tijd) is er geen onderbreking.
    if (vorige.eindDatum) {
      const grens = maandenErbij(vorige.eindDatum, params.onderbrekingResetMaanden)
      if (huidige.startDatum.getTime() > grens.getTime()) {
        resetMomenten.push(huidige.startDatum)
        chainStartIdx = i
      }
    }
  }

  const keten = contracten.slice(chainStartIdx)
  const aantalContracten = keten.filter((c) => c.bepaaldeTijd).length
  const eersteStart = keten[0].startDatum
  const laatste = keten[keten.length - 1]
  // Keten-span INCLUSIEF de laatste einddatum (zie contractDuurMaandenInclusief);
  // bij een onbepaald laatste contract (geen einddatum) is de ketenduur niet meer
  // relevant - dan meten we tot de startdatum.
  const ketenduurMaanden = laatste.eindDatum
    ? contractDuurMaandenInclusief(eersteStart, laatste.eindDatum)
    : heleMaandenTussen(eersteStart, laatste.startDatum)

  const volgendVerplichtOnbepaald =
    aantalContracten >= params.maxContracten || ketenduurMaanden > params.maxKetenMaanden

  return {
    aantalContracten,
    ketenduurMaanden,
    resetMomenten,
    volgendVerplichtOnbepaald,
    mogelijkOnvolledig,
  }
}
